import React from 'react'
import { useSearchParams } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { Cpu as DevicesIcon, Zap as BoltIcon } from 'lucide-react'
import HeaderTitleContent from '../../../common/components/atom/HeaderTitleContent/HeaderTitleContent'
import MetricCard from '../../../common/components/atom/MetricCard/MetricCard'
import QuickRangeSelector from '../../../features/dashboards/components/QuickRangeSelector'
import LocationMultiSelect from '../../../features/dashboards/components/LocationMultiSelect'
import EvolutionLinePresenter from '../../../features/dashboards/components/EvolutionLinePresenter'
import LocationsBarPresenter from '../../../features/dashboards/components/LocationsBarPresenter'
import HistogramPresenter from '../../../features/dashboards/components/HistogramPresenter'
import PieClassPresenter from '../../../features/dashboards/components/PieClassPresenter'
import {
  useJoulyDailyKpi,
  useJoulyDailyEvolution,
  useJoulyDailyBar,
  useJoulyDailyHistogram,
  useJoulyDailyPie,
} from '../../../features/dashboards/service/joulyDailyAPI'
import { useTotalInstalledMeters } from '../../../features/dashboards/service/installedMetersAPI'
import { useMinigridNamesByUuid } from '../../../features/dashboards/service/useMinigridNamesByUuid'
import type { Range, JoulyDailyFilter } from '../../../features/dashboards/types/joulyDailyTypes'

const DEFAULT_RANGE: Range = '30d'
const VALID_RANGES: ReadonlySet<Range> = new Set<Range>(['7d', '30d', '90d'])

function parseRange(raw: string | null): Range {
  return raw && VALID_RANGES.has(raw as Range) ? (raw as Range) : DEFAULT_RANGE
}

export default function DashboardDefaultPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const range: Range = parseRange(searchParams.get('range'))
  const uuidsParam = searchParams.get('uuids') ?? ''
  const uuids: string[] = uuidsParam.length > 0 ? uuidsParam.split(',') : []

  const filter: JoulyDailyFilter = { range, uuids }

  const handleRangeChange = (r: Range) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('range', r)
      return next
    })
  }

  const handleUuidsChange = (next: string[]) => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      if (next.length > 0) {
        out.set('uuids', next.join(','))
      } else {
        out.delete('uuids')
      }
      return out
    })
  }

  // KPI hooks — enabled only when uuids are selected (enforced inside hooks)
  const totalDevices = useJoulyDailyKpi('total-devices', filter)
  const meanConsumption = useJoulyDailyKpi('mean-consumption', filter)
  // Compteur "installés" : aligné sur les minigrids sélectionnés. Les uuids
  // (momi_uuid) sont résolus en noms canoniques via le catalogue ; chaque nom
  // est passé à `/meters/jouly/count` qui fait le matching côté backend
  // (nom + LinkedLocationNames). Sans sélection → total brut.
  const { resolveName } = useMinigridNamesByUuid()
  const selectedMinigridNames = uuids.map(resolveName).filter((n) => Boolean(n))
  const installedTotal = useTotalInstalledMeters(selectedMinigridNames)

  // Chart hooks
  const evolution = useJoulyDailyEvolution(filter)
  const bar = useJoulyDailyBar(filter)
  const histogram = useJoulyDailyHistogram(filter)
  const pie = useJoulyDailyPie(filter)

  const hasUuids = uuids.length > 0

  return (
    <div>
      <HeaderTitleContent>Dashboard — Default</HeaderTitleContent>

      {/* Filter row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          padding: '8px 24px 16px',
        }}
      >
        <QuickRangeSelector value={range} onChange={handleRangeChange} />
        <LocationMultiSelect value={uuids} onChange={handleUuidsChange} />
      </div>

      {/* Empty state */}
      {!hasUuids && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
            padding: 24,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Sélectionnez au moins un minigrid
          </Typography>
        </div>
      )}

      {/* Main content — only shown when uuids are selected */}
      {hasUuids && (
        <div style={{ padding: '0 24px' }}>
          <Grid container spacing={3}>
            {/* KPI row */}
            <Grid item xs={12} sm={6}>
              <MetricCard
                title="Nombre d'appareils captés / installés"
                value={(() => {
                  const captured =
                    totalDevices.isLoading
                      ? '…'
                      : totalDevices.data != null
                      ? String(Math.round(totalDevices.data.value))
                      : '—'
                  const installed =
                    installedTotal.isLoading
                      ? '…'
                      : installedTotal.data != null
                      ? String(installedTotal.data)
                      : '—'
                  return `${captured} / ${installed}`
                })()}
                unit=""
                showPercentage={false}
                icon={<DevicesIcon />}
                color="green"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <MetricCard
                title="Consommation moyenne"
                value={
                  meanConsumption.isLoading
                    ? '…'
                    : meanConsumption.data != null
                    ? meanConsumption.data.value.toFixed(2)
                    : '—'
                }
                unit=" kWh"
                showPercentage={false}
                icon={<BoltIcon />}
                color="yellow"
              />
            </Grid>

            {/* Evolution line — full width */}
            <Grid item xs={12}>
              <EvolutionLinePresenter
                data={evolution.data?.series ?? []}
                loading={evolution.isLoading}
                error={evolution.error as Error | null}
              />
            </Grid>

            {/* Bar + Pie side by side */}
            <Grid item xs={12} md={6}>
              <LocationsBarPresenter
                data={bar.data?.bars ?? []}
                loading={bar.isLoading}
                error={bar.error as Error | null}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <PieClassPresenter
                data={pie.data?.slices ?? []}
                loading={pie.isLoading}
                error={pie.error as Error | null}
              />
            </Grid>

            {/* Histogram — full width */}
            <Grid item xs={12}>
              <HistogramPresenter
                data={histogram.data?.bins ?? []}
                loading={histogram.isLoading}
                error={histogram.error as Error | null}
              />
            </Grid>
          </Grid>
        </div>
      )}
    </div>
  )
}
